import React, { useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { AsyncDisplay, LoadingMessage } from 'js/components';
import { Card, CardBody, CardHeader } from 'reactstrap';
import OrgTrendsChart from './OrgTrendsChart';
import OrgTrendsHeader from './OrgTrendsHeader';
import QuestionSet from './QuestionSet';
import { OrgTrendsProvider, useOrgTrends } from './OrgTrendsContext';
import {
  buildOrgTrendsFromNative,
  buildOrgTrendsFromRubric,
  buildOrgTrendsFromSnapshots,
  buildOrgTrendsCategoryLookups,
  getTrendSeriesForChart,
} from 'js/utils/org-trends';
import { buildOrgTrendsChartLines } from 'js/utils/org-trends-rollup';
import { createOrgAssessmentWeightContext } from 'js/utils/org-assessment-rollup';

import styles from './OrgTrends.module.scss';

function OrgTrendsBody({ window, questionSets, teams, lineHoverMaxDistance }) {
  const {
    expandedKeys,
    toggleExpanded,
    organization,
    selectedPickerRow,
    debounceTrendPoints,
  } = useOrgTrends();

  const isSingleQuestionSet = useMemo(
    () => questionSets.length === 1,
    [questionSets]
  );

  const singleQuestionSet = useMemo(() => {
    return questionSets.length === 1
      ? { ...questionSets[0], name: questionSets[0].name ?? 'Overall score' }
      : null;
  });

  const showQuestionSetSections = useMemo(
    () => questionSets.length > 1,
    [questionSets]
  );

  const weightContext = useMemo(
    () => createOrgAssessmentWeightContext(organization),
    [organization]
  );

  const overallLines = useMemo(
    () =>
      buildOrgTrendsChartLines({
        organization,
        teams,
        weightContext,
        selectedPickerRow,
        getTeamSeries: (series) => series?.overall,
        getTrendSeriesForChart,
        window,
        debounceTrendPoints,
      }),
    [
      teams,
      organization,
      selectedPickerRow,
      weightContext,
      window,
      debounceTrendPoints,
    ]
  );

  if (!overallLines.length) {
    return (
      <div className={styles['org-trends']}>
        <OrgTrendsHeader showQuestionSetSections={showQuestionSetSections} />
        <Card>
          <CardBody>No trend data for the selected org node.</CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles['org-trends']}>
      <OrgTrendsHeader showQuestionSetSections={showQuestionSetSections} />
      {singleQuestionSet ? (
        <QuestionSet
          questionSet={singleQuestionSet}
          teams={teams}
          window={window}
          expanded={true}
          onToggle={toggleExpanded}
          lineHoverMaxDistance={lineHoverMaxDistance}
          allowQuestionCollapse={false}
        />
      ) : (
        <>
          <Card>
            <CardHeader className={styles['section-header']}>
              Overall score
            </CardHeader>
            <CardBody>
              <div
                className={`${styles['chart-wrapper-large']} ${styles['overall-chart']}`}
              >
                <OrgTrendsChart
                  window={window}
                  trendLines={overallLines}
                  lineHoverMaxDistance={lineHoverMaxDistance}
                />
              </div>
            </CardBody>
          </Card>

          {questionSets.map((questionSet) => (
            <QuestionSet
              key={questionSet.key}
              questionSet={questionSet}
              teams={teams}
              window={window}
              expanded={expandedKeys.has(questionSet.key)}
              onToggle={toggleExpanded}
              lineHoverMaxDistance={lineHoverMaxDistance}
            />
          ))}
        </>
      )}
    </div>
  );
}

function OrgTrends({
  isRubric = false,
  orgRubricScores = null,
  nativeTrends = null,
  rubricDocument = null,
  assessmentTemplate = null,
  snapshotCompare = null,
  assessmentKey: assessmentKeyProp = null,
  buildPickerPath = null,
  debounceTrendPoints = true,
  lineHoverMaxDistance,
  organization,
  workspace,
  match,
  history,
  location,
  loading = false,
  error = null,
}) {
  const trendData = useMemo(() => {
    if (snapshotCompare) {
      return buildOrgTrendsFromSnapshots(snapshotCompare);
    }

    if (isRubric) {
      return buildOrgTrendsFromRubric({
        teams: orgRubricScores?.teams,
        document: rubricDocument ?? orgRubricScores?.document,
      });
    }

    return buildOrgTrendsFromNative(nativeTrends, assessmentTemplate);
  }, [
    assessmentTemplate,
    isRubric,
    orgRubricScores,
    nativeTrends,
    rubricDocument,
    snapshotCompare,
  ]);

  const { window, questionSets, teams } = trendData;

  const questionSetKeys = useMemo(
    () => questionSets.map((qs) => qs.key),
    [questionSets]
  );

  const assessmentKey = assessmentKeyProp ?? match?.params?.assessmentKey;

  const categoryLookups = useMemo(
    () =>
      buildOrgTrendsCategoryLookups({
        isRubric: isRubric || !!snapshotCompare,
        assessmentTemplate,
        questionSets,
      }),
    [assessmentTemplate, isRubric, questionSets, snapshotCompare]
  );

  return (
    <AsyncDisplay isLoading={loading} isError={!!error}>
      <AsyncDisplay.Loading>
        <LoadingMessage iconSize="2x">Loading &hellip;</LoadingMessage>
      </AsyncDisplay.Loading>
      <AsyncDisplay.Error>
        <div>Sorry, there was a problem loading the details.</div>
      </AsyncDisplay.Error>
      <AsyncDisplay.Resolved>
        {!window || !teams.length ? (
          <Card>
            <CardBody>No trend data available.</CardBody>
          </Card>
        ) : (
          <OrgTrendsProvider
            organization={organization}
            workspace={workspace}
            assessmentKey={assessmentKey}
            match={match}
            history={history}
            location={location}
            questionSetKeys={questionSetKeys}
            categoryLookups={categoryLookups}
            buildPickerPath={buildPickerPath}
            debounceTrendPoints={debounceTrendPoints}
          >
            <OrgTrendsBody
              window={window}
              questionSets={questionSets}
              teams={teams}
              lineHoverMaxDistance={lineHoverMaxDistance}
            />
          </OrgTrendsProvider>
        )}
      </AsyncDisplay.Resolved>
    </AsyncDisplay>
  );
}

export default withRouter(OrgTrends);
